import { viewTransition } from '@theme/utilities';
import { Component } from '@theme/component';

const GUTTER = 16;
const GAP = 6;

export class FloatingPanelComponent extends Component {
  /** @type {HTMLDetailsElement | null} */
  #details = null;
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {number | null} */
  #frame = null;

  #queue = () => {
    if (!this.isConnected || this.#frame !== null) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#place();
    });
  };

  #clearPosition = () => {
    for (const name of ['left', 'top', 'max-height']) this.style.removeProperty(name);
  };

  #onResize = () => {
    if (getComputedStyle(this).position !== 'absolute') {
      this.#clearPosition();
      return;
    }
    if (this.hasAttribute('data-close-on-resize') && this.dataset.closeOnResize !== 'false') {
      if (this.#details) this.#details.open = false;
    }
    this.#queue();
  };

  #place = async () => {
    const signal = this.#abort?.signal;
    if (viewTransition.current) await viewTransition.current;
    if (signal?.aborted || !this.isConnected) return;
    if (getComputedStyle(this).position !== 'absolute') {
      this.#clearPosition();
      return;
    }
    const details = this.#details;
    const summary = details?.querySelector(':scope > summary');
    if (!details?.open || !(summary instanceof HTMLElement) || !this.getClientRects().length) return;

    const viewport = window.visualViewport;
    const viewportLeft = viewport?.offsetLeft ?? 0;
    const viewportTop = viewport?.offsetTop ?? 0;
    const viewportRight = viewportLeft + (viewport?.width ?? window.innerWidth);
    const viewportBottom = viewportTop + (viewport?.height ?? window.innerHeight);
    const anchor = summary.getBoundingClientRect();
    if (anchor.bottom <= viewportTop || anchor.top >= viewportBottom) {
      details.open = false;
      return;
    }
    const header = document.querySelector('#header-component')?.getBoundingClientRect();
    const topLimit = header && header.top <= viewportTop && header.bottom > viewportTop
      ? Math.max(viewportTop + GUTTER, header.bottom + GAP)
      : viewportTop + GUTTER;
    const below = Math.max(0, viewportBottom - GUTTER - anchor.bottom - GAP);
    const above = Math.max(0, anchor.top - GAP - topLimit);
    const placeAbove = below < 200 && above > below;
    const available = Math.floor(placeAbove ? above : below);

    this.style.left = '0px';
    this.style.top = `${summary.offsetTop + summary.offsetHeight + GAP}px`;
    this.style.maxHeight = `min(var(--facets-panel-height, 420px), ${available}px)`;
    const rect = this.getBoundingClientRect();
    const minLeft = viewportLeft + GUTTER;
    const maxLeft = Math.max(minLeft, viewportRight - GUTTER - rect.width);
    const left = Math.min(Math.max(rect.left, minLeft), maxLeft);
    this.style.left = `${Math.round(left - rect.left)}px`;
    if (placeAbove) this.style.top = `${summary.offsetTop - GAP - rect.height}px`;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    this.#details = this.closest('details');
    const { signal } = this.#abort;
    this.#details?.addEventListener('toggle', this.#queue, { capture: true, signal });
    window.addEventListener('resize', this.#onResize, { passive: true, signal });
    window.addEventListener('scroll', this.#queue, { capture: true, passive: true, signal });
    window.visualViewport?.addEventListener('resize', this.#onResize, { passive: true, signal });
    this.#resizeObserver = new ResizeObserver(this.#queue);
    this.#resizeObserver.observe(this);
    const content = this.firstElementChild;
    if (content) this.#resizeObserver.observe(content);
    this.#queue();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#queue();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#resizeObserver?.disconnect();
    if (this.#frame !== null) cancelAnimationFrame(this.#frame);
    this.#frame = null;
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
